import { useCallback, useMemo } from "react";
import { get } from "lodash";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import tagConstants from "constants/Tag";
import {
  getContactsTableColumns,
  getContactsTableFilters,
} from "routes/contact/components/contacts_table/ContactsTableColumnsAndFilters";
import { contactKeys } from "routes/contact/components/contacts_table/queryKeys";
import EntityService from "services/Entity";
import TagService from "services/Tag";
import UserService from "services/User";
import ContactService from "services/Contact";
import CustomFieldService from "services/CustomField";
import VoicemailService from "services/Voicemails";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";

export const handleFetchTags = async (className, forFiltering = true) => {
  const taggableModels = tagConstants["taggableModels"];
  const model =
    className === "EmailScheduleContacts"
      ? "contact"
      : Object.keys(taggableModels).find(
          key => taggableModels[key] === className
        );
  const tags = await TagService.getTagOptions({ model, forFiltering });

  return tags;
};

const handleFetchCustomFields = async () => {
  const customFields = await ContactService.getCustomFields();
  const entitiesCustomFields = await EntityService.getCustomFields();
  const contactConfigs =
    await CustomFieldService.getCustomFieldConfigsForModel("Contact");
  const entityConfigs =
    await CustomFieldService.getCustomFieldConfigsForModel("Entity");

  return [
    {
      accessorPrefix: "custom_fields",
      fields: customFields,
      model: "Contact",
      headerPrefix: "Contact",
      configs: contactConfigs,
    },
    {
      accessorPrefix: "entity_custom_fields",
      headerPrefix: "Entity",
      fields: entitiesCustomFields,
      model: "Entity",
      configs: entityConfigs,
    },
  ];
};

const handleFetchVoicemails = async () => {
  const voicemails = await VoicemailService.getVoicemailsForFilter();
  return voicemails;
};

export const useContactsTable = ({
  className = "Contact",
  enableCalling = false,
  tableName = "contacts",
  entityId = null,
}) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", "options", className],
    queryFn: () => handleFetchTags(className),
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", className],
    queryFn: handleFetchCustomFields,
  });

  const { data: voicemails, isSuccess: isVoicemailsFetched } = useQuery({
    queryKey: ["voicemails"],
    queryFn: handleFetchVoicemails,
    enabled: enableCalling,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(() => contactKeys.list({ entityId }), [entityId]);

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const handleDeleteContact = useCallback(
    async (entityId, contactId) => {
      await EntityService.deleteContact(entityId, contactId);
      await invalidateData();
    },
    [invalidateData]
  );

  const filters = useMemo(() => {
    if (!isTagsFetched || !isCustomFieldsFetched) {
      return [];
    }
    const finalFilters = getContactsTableFilters({ tags, entityId });

    if (isCustomFieldsFetched) {
      finalFilters.push(
        ...buildCustomFieldFilters(
          customFields,
          finalFilters.map(f => f.title)
        )
      );
    }

    return finalFilters;
  }, [tags, customFields, isTagsFetched, isCustomFieldsFetched, entityId]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getContactsTableColumns({
      handleDeleteContact,
      invalidateData,
      voicemails,
      enableCalling,
      isVoicemailsFetched,
    });

    const contactsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: contactsTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: contactsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    customFields,
    tableSettings,
    voicemails,
    isTableSettingsFetched,
    isCustomFieldsFetched,
    isVoicemailsFetched,
    tableName,
    enableCalling,
    handleDeleteContact,
    invalidateData,
  ]);
  return { filters, columns, tags, customFields, queryKey, invalidateData };
};
